import React, { useState } from "react";
import { toast } from "react-hot-toast";

/**
 * Shows a post/comment's text with Edit / Delete controls.
 *
 * - canEdit / canDelete decide which buttons appear (author vs. moderator).
 * - onSave({ text, title }) and onDelete() should return promises; errors are
 *   caught here and shown as a toast.
 * - Pass `title` (a string) to also edit a title field (wall notes).
 */
export default function EditableText({
  text,
  title,
  edited,
  canEdit,
  canDelete,
  onSave,
  onDelete,
  textClassName = "commentary-text",
  textStyle,
  renderTitle,
  itemLabel = "post",
  compact = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text || "");
  const [draftTitle, setDraftTitle] = useState(title || "");
  const [busy, setBusy] = useState(false);
  const hasTitle = title !== undefined;

  const startEdit = () => {
    setDraft(text || "");
    setDraftTitle(title || "");
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!draft.trim() || (hasTitle && !draftTitle.trim())) return;
    setBusy(true);
    try {
      await onSave({ text: draft.trim(), title: draftTitle.trim() });
      setEditing(false);
      toast.success("Saved!");
    } catch (err) {
      console.error(`Failed to edit ${itemLabel}:`, err);
      toast.error(`Could not save your ${itemLabel}. Please try again.`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this ${itemLabel}? This can't be undone.`)) return;
    setBusy(true);
    try {
      await onDelete();
      toast.success("Deleted.");
    } catch (err) {
      console.error(`Failed to delete ${itemLabel}:`, err);
      toast.error(`Could not delete this ${itemLabel}. Please try again.`);
      setBusy(false);
    }
  };

  const btn = {
    background: "none",
    border: "none",
    padding: compact ? "2px 6px" : "4px 8px",
    color: "#0D3B8C",
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: compact ? "12px" : "14px",
    fontFamily: "sans-serif",
  };

  if (editing) {
    return (
      <form onSubmit={save} style={{ margin: "8px 0" }}>
        {hasTitle && (
          <input
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="Title"
            disabled={busy}
            style={{ width: "90%", padding: "8px", fontSize: "15px", borderRadius: "6px", border: "1px solid #ccc", marginBottom: "8px", boxSizing: "border-box" }}
          />
        )}
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          autoFocus
          rows={compact ? 2 : 4}
          style={{ width: "90%", padding: "8px", fontSize: "15px", borderRadius: "6px", border: "1px solid #ccc", boxSizing: "border-box", resize: "vertical", margin: 0 }}
        />
        <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "6px" }}>
          <button type="submit" className="respond-btn" style={{ marginTop: 0, cursor: "pointer" }} disabled={busy || !draft.trim() || (hasTitle && !draftTitle.trim())}>
            {busy ? "Saving…" : "Save"}
          </button>
          <button type="button" className="respond-btn" style={{ marginTop: 0, cursor: "pointer", background: "#888" }} onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    );
  }

  return (
    <>
      {hasTitle && renderTitle ? renderTitle(title) : null}
      <p className={textClassName} style={textStyle}>
        {text}
        {edited && (
          <span style={{ fontSize: "0.75em", color: "#888", fontStyle: "italic", marginLeft: "6px" }}>(edited)</span>
        )}
      </p>
      {(canEdit || canDelete) && (
        <div style={{ display: "flex", gap: "4px", justifyContent: compact ? "flex-start" : "center" }}>
          {canEdit && <button type="button" style={btn} onClick={startEdit} disabled={busy}>Edit</button>}
          {canDelete && <button type="button" style={{ ...btn, color: "#B91C1C" }} onClick={remove} disabled={busy}>{busy ? "Deleting…" : "Delete"}</button>}
        </div>
      )}
    </>
  );
}
